import { Component, inject, Input, OnChanges, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ContentType,
  DsPaginationV2Module,
  DsTableModule,
  SortType,
  TableCellData,
  TableCellTextAlign,
  TableColumnConfig,
  TableRow,
} from '@levelaccess/design-system';
import { IChatV2ComponentChunk } from '@shared/interfaces/ai-executor.interface';
import { AiChatNavigationService } from '../../ai-chat-navigation.service';
import { AiChatStateService } from '../../ai-chat-state.service';
import { toMarkdownTable, toPlatformPath } from '../generative-components.utils';

export interface ILinkCell {
  value: string | number | boolean | null;
  href: string;
}

function isLinkCell(cell: unknown): cell is ILinkCell {
  return cell !== null && typeof cell === 'object' && 'href' in (cell as Record<string, unknown>);
}

function cellDisplayValue(cell: unknown): string | number | boolean | null {
  return isLinkCell(cell) ? cell.value : (cell as string | number | boolean | null);
}

function cellDisplayText(cell: unknown): string {
  const value = cellDisplayValue(cell);
  return value === null || value === undefined ? '' : String(value);
}

const PAGINATION_THRESHOLD = 10;

@Component({
  selector: 'app-ai-chat-table',
  templateUrl: './ai-chat-table.component.html',
  styleUrl: './ai-chat-table.component.scss',
  imports: [DsTableModule, DsPaginationV2Module],
  standalone: true,
})
export class AiChatTableComponent implements OnChanges {
  @Input() public chunk: IChatV2ComponentChunk;

  private readonly aiChatNavigationService: AiChatNavigationService = inject(AiChatNavigationService);
  private readonly aiChatStateService: AiChatStateService = inject(AiChatStateService);

  public readonly isExpanded: Signal<boolean> = toSignal(this.aiChatStateService.showExpandedPanel$, { initialValue: false });

  public columns: TableColumnConfig[] = [];
  public allRows: TableRow[] = [];
  public pagedRows: TableRow[] = [];
  public caption: string = '';
  public currentPage: number = 1;
  public pageSize: number = PAGINATION_THRESHOLD;
  public readonly paginationThreshold: number = PAGINATION_THRESHOLD;

  private isNumericValue(value: unknown): boolean {
    const displayValue = cellDisplayValue(value);
    if (typeof displayValue === 'number') {
      return true;
    }
    if (typeof displayValue === 'string' && displayValue.trim() !== '') {
      return /^-?\d+(\.\d+)?%?$/.test(displayValue.trim());
    }
    return false;
  }

  public static serialize(data: Record<string, unknown>): string {
    const columns = (data['columns'] as string[]) ?? [];
    const rows = (data['rows'] as unknown[][]) ?? [];
    const caption = (data['caption'] as string) ?? 'Results';

    const textRows = rows.map((cells: unknown[]) => cells.map(cellDisplayText));

    return [caption, toMarkdownTable(columns, textRows)].join('\n');
  }

  public ngOnChanges(): void {
    const data = this.chunk?.data ?? {};
    const columnHeaders = (data['columns'] as string[]) ?? [];
    const rowValues = (data['rows'] as unknown[][]) ?? [];
    this.caption = (data['caption'] as string) ?? 'Results';
    this.currentPage = 1;

    this.columns = columnHeaders.map((header: string, i: number) => {
      const isNumeric = rowValues.some((cells: unknown[]) => this.isNumericValue(cells[i]));
      const hasLinks = rowValues.some((cells: unknown[]) => isLinkCell(cells[i]));
      return {
        key: header,
        header,
        textAlign: isNumeric ? TableCellTextAlign.right : TableCellTextAlign.left,
        sortable: isNumeric,
        sortType: isNumeric ? SortType.number : undefined,
        contentType: hasLinks ? ContentType.link : undefined,
      };
    });

    this.allRows = rowValues.map((cells: unknown[]) => ({
      data: Object.fromEntries(
        columnHeaders.map((key: string, i: number): [string, TableCellData] => {
          const cell = cells[i];
          if (isLinkCell(cell)) {
            const { href } = cell;
            const platformPath = toPlatformPath(href);
            if (platformPath !== null) {
              const [path, search] = platformPath.split('?');
              const queryParams: Record<string, string> = search
                ? Object.fromEntries(new URLSearchParams(search).entries())
                : undefined;
              return [key, { value: cell.value ?? undefined, rtLink: path, queryParams }];
            }
            if (href.startsWith('http')) {
              return [
                key,
                { value: cell.value ?? undefined, url: href, attributes: { target: '_blank', rel: 'noopener noreferrer' } },
              ];
            }
            const [path, search] = href.split('?');
            const queryParams: Record<string, string> = search
              ? Object.fromEntries(new URLSearchParams(search).entries())
              : undefined;
            return [key, { value: cell.value ?? undefined, rtLink: path, queryParams }];
          }
          return [key, { value: (cell as string | number | boolean | null) ?? undefined }];
        }),
      ),
    }));

    this.updatePagedRows();
  }

  public onTableClick(event: MouseEvent): void {
    const anchor = (event.target as Element).closest('a');
    if (!anchor) {
      return;
    }
    const href = anchor.getAttribute('href');
    if (href && this.aiChatNavigationService.navigateTo(href)) {
      event.preventDefault();
    }
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePagedRows();
  }

  public onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.updatePagedRows();
  }

  private updatePagedRows(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.allRows.slice(start, start + this.pageSize);
  }
}
